/**
 * Keep Law Library research text out of Sentry (#1964, spec 7.5).
 *
 * The page keeps its query in its own URL (/law-library?q=…&statute_cite=…), and
 * @sentry/react records URLs in many places: an event's request.url and Referer,
 * navigation and xhr/fetch breadcrumbs, transaction and span data, and a replay
 * event's `urls`. Every string in those passes through scrubLawLibraryText: a
 * /law-library URL keeps only `tab` and `matter_id`, and an /api/law-library/* URL
 * loses its query string (continuation tokens, table-of-contents paths). A request
 * to /api/law-library/* also loses its body (the question, a citation, a chunk).
 * The research conversation routes (/api/research-workbench/*, the Ask tab) are treated the same way: their bodies carry questions.
 * The client-name searches (matters, contacts, duplicate/red-flag/conflict checks, quick, full, instant and
 * document search) POST their typed text since #2025/#2028: those bodies are dropped, and the old GET forms
 * lose their query string.
 * The research searches (case search, licensed manuals, Employee Manual search and Ask) do the same since #2028.
 *
 * Replay's DOM recording cannot be scrubbed from here (its hook sees custom events
 * only): the new page stops Replay while it is open (usePauseReplay), and the old
 * page's reader card is blocked with data-sentry-block.
 * The server side is Plan 2's maverick_telemetry scrub.
 */
import type { Breadcrumb, Event } from '@sentry/react';

export const REPLAY_BLOCK_SELECTOR = '[data-sentry-block]';

const KEPT = new Set(['tab', 'matter_id']);
// The firm-reference and licensed-manual searches put the typed words in a GET query string.
const API_QUERY = /(\/?api\/(?:law-library\/|legal-references|research-workbench\/)[^\s?#"'<>)]*)\?[^#"'<>]*/gi;
// R11: the matter search used to put typed text here. getMatters now POSTs it to /api/matters/search (#2025),
// but GET /api/matters still accepts `search` (Norris, and a stale bundle until reload), so the URL rule stays
// until that parameter is retired. The list route only.
const MATTERS_QUERY = /(\/?api\/matters\/?)\?[^#"'<>]*/gi;
// The other client-name searches used to put typed text in these GET query strings (#2028). The client now
// POSTs it, but the GET routes stay (older callers, a stale bundle until reload), so their URL rule stays too.
const SEARCH_GET_ROUTES = [
  'contacts',
  'contacts/duplicate-check',
  'matters/red-flag-matches',
  'conflict-check',
  'search',
  'search/full',
  'instant-search/(?:matters|documents|contacts)',
  'document-search',
  String.raw`matters/\d+/document-search`,
  // The research searches (#2028). The licensed-manual search's URL and span are already covered by the
  // legal-references rules (API_QUERY, QUERY_STRIPPED_PATHS).
  'courtlistener/search',
  'staff-portal/employee-manual/search',
].join('|');
const SEARCH_QUERY = new RegExp(String.raw`(/?api/(?:${SEARCH_GET_ROUTES})/?)\?[^#"'<>]*`, 'gi');
const SEARCH_GET_PATH = new RegExp(String.raw`^/api/(?:${SEARCH_GET_ROUTES})/?$`, 'i');
const PAGE_QUERY = /(\/law-library\/?)\?([^#"'<>]*)/gi;
const ENCODED_PAGE_QUERY = /(%2Flaw-library)%3F[^\s&#"'<>]*/gi;
const API_PATH = '/api/law-library/';
const RESEARCH_PATH = '/api/research-workbench/';
// The matter search's typed text (a client name) travels in this route's POST body since #2025.
const MATTERS_SEARCH_PATH = '/api/matters/search';
// The other client-name searches' POST bodies (#2028). '/api/search' covers /api/search/full; '/document-search'
// covers both the cross-matter and the per-matter route.
const NAME_SEARCH_BODY_PATHS = [
  '/api/contacts/search',
  '/api/contacts/duplicate-check',
  '/api/matters/red-flag-matches',
  '/api/conflict-check',
  '/api/search',
  '/api/instant-search/',
  '/document-search',
];
// The research searches' POST bodies (#2028): a case name or citation, a licensed-manual question, a policy query.
// The Employee Manual's Ask question is dropped too: it can name a client just as a search can.
const RESEARCH_SEARCH_BODY_PATHS = [
  '/api/courtlistener/search',
  '/api/legal-references/nclc/search',
  '/api/staff-portal/employee-manual/search',
  '/api/staff-portal/employee-manual/ask',
];
const BODY_DROPPED_PATHS = [
  API_PATH, RESEARCH_PATH, MATTERS_SEARCH_PATH, ...NAME_SEARCH_BODY_PATHS, ...RESEARCH_SEARCH_BODY_PATHS,
];
const QUERY_STRIPPED_PATHS = [API_PATH, '/api/legal-references', RESEARCH_PATH];
const BODY_KEYS = ['body', 'input', 'request_body'];
const MAX_DEPTH = 12;

function keptQuery(query: string): string {
  const kept = new URLSearchParams();
  for (const [name, value] of new URLSearchParams(query)) {
    if (KEPT.has(name)) kept.append(name, value);
  }
  const text = kept.toString();
  return text ? `?${text}` : '';
}

export function scrubLawLibraryText(value: string): string {
  const lower = value.toLowerCase();
  if (!lower.includes('law-library') && !lower.includes('legal-references') && !lower.includes('research-workbench')
    && !lower.includes('api/matters?') && !lower.includes('api/matters/?') && !(lower.includes('api/') && lower.includes('?'))) {
    return value;
  }
  return value
    .replace(API_QUERY, '$1')
    .replace(MATTERS_QUERY, '$1')
    .replace(SEARCH_QUERY, '$1')
    .replace(PAGE_QUERY, (_whole, path: string, query: string) => `${path}${keptQuery(query)}`)
    .replace(ENCODED_PAGE_QUERY, '$1');
}

/** Every string inside `value`, rewritten in place. Never throws: Sentry must keep working. */
export function scrubLawLibraryValue<T>(value: T, depth = 0): T {
  if (typeof value === 'string') return scrubLawLibraryText(value) as T;
  if (!value || typeof value !== 'object' || depth > MAX_DEPTH) return value;
  try {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        try {
          value[index] = scrubLawLibraryValue(item, depth + 1);
        } catch {
          // One frozen element must not stop the walk.
        }
      });
      return value;
    }
    const record = value as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      try {
        record[key] = scrubLawLibraryValue(record[key], depth + 1);
      } catch {
        // One frozen property must not stop the walk.
      }
    }
  } catch {
    // A frozen object: leave it rather than lose the event.
  }
  return value;
}

function isLawLibraryApi(url: unknown): boolean {
  return typeof url === 'string' && BODY_DROPPED_PATHS.some((path) => url.includes(path));
}

function dropBreadcrumbBody(breadcrumb: Breadcrumb): void {
  const data = breadcrumb.data as Record<string, unknown> | undefined;
  if (data && isLawLibraryApi(data.url)) {
    for (const key of BODY_KEYS) delete data[key];
  }
}

const MATTERS_LIST_PATH = '/api/matters';

/** The billing dialog's matter search: the list route itself, nothing after it (T2). */
export function isMattersListUrl(url: string): boolean {
  try {
    const path = new URL(url, 'https://valentine.invalid/').pathname;
    return path === MATTERS_LIST_PATH || path === `${MATTERS_LIST_PATH}/`;
  } catch {
    return false;
  }
}

/** One of the GET routes the client-name and research searches used before #2028. */
export function isSearchGetUrl(url: string): boolean {
  try {
    return SEARCH_GET_PATH.test(new URL(url, 'https://valentine.invalid/').pathname);
  } catch {
    return false;
  }
}

function dropSpanQuery(span: { data?: unknown }): void {
  const data = span.data as Record<string, unknown> | undefined;
  if (!data) return;
  const url = [data.url, data['http.url']].find((candidate) => typeof candidate === 'string') as string | undefined;
  if (url && (QUERY_STRIPPED_PATHS.some((path) => url.includes(path)) || isMattersListUrl(url) || isSearchGetUrl(url))) {
    delete data['http.query'];
    delete data['http.fragment'];
  }
}

export function scrubLawLibraryEvent<E extends Event>(event: E): E {
  try {
    if (event.request && isLawLibraryApi(event.request.url)) delete event.request.data;
    event.breadcrumbs?.forEach(dropBreadcrumbBody);
    event.spans?.forEach(dropSpanQuery);
  } catch {
    // As above: never lose the event.
  }
  return scrubLawLibraryValue(event);
}

/**
 * Replay's own navigation entry is a custom recording event (type 5, tag 'breadcrumb')
 * naming /law-library?q=… at pushState time. Scrubbed in place; never dropped.
 */
export function scrubReplayRecordingEvent<E>(event: E): E {
  return scrubLawLibraryValue(event);
}

export function scrubLawLibraryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  try {
    dropBreadcrumbBody(breadcrumb);
  } catch {
    // As above.
  }
  return scrubLawLibraryValue(breadcrumb);
}
